import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from 'react-query';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import Layout from './components/Layout/Layout';

// Auth Components
import Login from './components/Auth/Login';

// Dashboard
import Dashboard from './components/Dashboard/Dashboard';

// Customer Components
import Customers from './components/Customers/Customers';
import CustomerForm from './components/Customers/CustomerForm';
import CustomerEditPage from './components/Customers/CustomerEditPage';
import CustomerView from './components/Customers/CustomerView';

// Proposal Components
import Proposals from './components/Proposals/Proposals';
import ProposalForm from './components/Proposals/ProposalForm';
import ProposalEditPage from './components/Proposals/ProposalEditPage';
import ProposalView from './components/Proposals/ProposalView';

// Template Components
import Templates from './components/Templates/Templates';
import TemplateForm from './components/Templates/TemplateForm';

// Email Template Components
import EmailTemplates from './components/EmailTemplates/EmailTemplates';
import EmailTemplateForm from './components/EmailTemplates/EmailTemplateForm';

// Profile Components
import Profile from './components/Profile/Profile';

// Settings Components
import Settings from './components/Settings/Settings';

// User Components
import Users from './components/Users/Users';

// Public Components
import PublicProposal from './components/PublicProposal/PublicProposal';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// App content component that uses theme
const AppContent = () => {
  const { theme } = useTheme();
  
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SettingsProvider>
          <Router>
            <Box sx={{ display: 'flex', minHeight: '100vh', maxWidth: 'none', width: '100%' }}>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/proposal/:uuid" element={<PublicProposal />} />
                
                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          {/* Dashboard */}
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          
                          {/* Customers */}
                          <Route path="/customers" element={<Customers />} />
                          <Route path="/customers/new" element={<CustomerForm />} />
                          <Route path="/customers/edit/:id" element={<CustomerEditPage />} />
                          <Route path="/customers/:id" element={<CustomerView />} />
                          
                          {/* Proposals */}
                          <Route path="/proposals" element={<Proposals />} />
                          <Route path="/proposals/new" element={<ProposalForm />} />
                          <Route path="/proposals/edit/:id" element={<ProposalEditPage />} />
                          <Route path="/proposals/view/:id" element={<ProposalView />} />
                          <Route path="/proposals/:id" element={<ProposalView />} />
                          
                          {/* Templates */}
                          <Route path="/templates" element={<Templates />} />
                          <Route path="/templates/new" element={<TemplateForm />} />
                          <Route path="/templates/:id" element={<TemplateForm />} />
                          <Route path="/templates/:id/edit" element={<TemplateForm />} />
                          
                          {/* Email Template Routes */}
                          <Route path="/email-templates" element={<EmailTemplates />} />
                          <Route path="/email-templates/new" element={<EmailTemplateForm />} />
                          <Route path="/email-templates/view/:id" element={<EmailTemplateForm />} />
                          <Route path="/email-templates/edit/:id" element={<EmailTemplateForm />} />
                          
                          {/* Profile */}
                          <Route path="/profile" element={<Profile />} />
                          
                          {/* Settings */}
                          <Route path="/settings" element={<Settings />} />
                          
                          {/* Users (Admin only) */}
                          <Route
                            path="/users"
                            element={
                              <ProtectedRoute requireAdmin>
                                <Users />
                              </ProtectedRoute>
                            }
                          />
                          
                          {/* Catch all - redirect to dashboard */}
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Box>
            
            {/* Toast Notifications */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme={theme.palette.mode}
            />
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;